import React, { useState, useEffect, useRef } from 'react';
import style from './StepsRecordList.module.css';
import { Link, useNavigate } from 'react-router-dom';
import HeadSub from '../../../../../components/comm/HeadSub';
import axiosApi from '../../../../../api/daonAxios';
import CODE from '../../../../../constants/code';

const StepsRecordList = () => {
    const navigate = useNavigate();

    const [allRecords, setAllRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const isLoadingRef = useRef(false);
    const currentYearRef = useRef(new Date().getFullYear());
    const currentMonthRef = useRef(new Date().getMonth() + 1);
    const listEndRef = useRef(null);

    const calculateCalories = (steps) => Math.round(steps * 0.04);
    const calculateDistance = (steps) => (steps * 0.0008).toFixed(1);

    const formatDate = (dateString) => {
        if (typeof dateString === 'string' && dateString.length === 8) {
            const shortYear = dateString.substring(2, 4);
            const month = dateString.substring(4, 6);
            const day = dateString.substring(6, 8);
            return `${shortYear}.${month}.${day}`;
        }

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        const year = String(date.getFullYear()).slice(-2);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}.${month}.${day}`;
    };

    const convertApiDataToRecords = (apiData) => {
        if (!apiData || !Array.isArray(apiData)) return [];

        return apiData
            .filter(item => item.steps > 0)
            .map(item => {
                const recDe = item.recDe || item.rec_de;
                return {
                    date: recDe,
                    steps: item.steps,
                    calories: item.cnsumCalor || item.cnsum_calor || calculateCalories(item.steps),
                    distance: item.walkDstnc || item.walk_dstnc || calculateDistance(item.steps),
                    displayDate: formatDate(recDe)
                };
            });
    };

    const fetchMonthData = async (year, month) => {
        try {
            const url = "/health/steps/month";
            const jToken = localStorage.getItem('jToken');
            const loginUser = sessionStorage.getItem("loginUser");

            if (!jToken || !loginUser) {
                throw new Error('로그인이 필요합니다.');
            }

            const user = JSON.parse(loginUser);
            const reqOptions = {
                headers: { Authorization: `Bearer ${jToken}` }
            };
            const reqBody = {
                usrInfoId: user?.usrInfoId,
                year: year,
                month: month
            };

            const res = await axiosApi.post(url, reqBody, reqOptions);

            if (res.data.resultCode === CODE.RCV_SUCCESS) {
                const apiData = res.data.result.healthWalkInfo || [];
                return convertApiDataToRecords(apiData);
            }

            return [];
        } catch (error) {
            if (error.response?.status === 401) {
                localStorage.removeItem('jToken');
                sessionStorage.removeItem('loginUser');
                navigate('/login');
                return [];
            }
            throw error;
        }
    };

    const loadInitialData = async () => {
        try {
            setLoading(true);
            setError(null);
            isLoadingRef.current = true;

            const records = [];
            const today = new Date();

            for (let i = 0; i < 3; i++) {
                const targetDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
                const year = targetDate.getFullYear();
                const month = targetDate.getMonth() + 1;

                try {
                    const monthRecords = await fetchMonthData(year, month);
                    if (monthRecords.length > 0) {
                        records.push(...monthRecords);
                    }
                } catch (monthError) {
                    console.error(`${year}년 ${month}월 개별 오류:`, monthError);
                }

                if (i < 2) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }

            records.sort((a, b) => b.date.localeCompare(a.date));
            setAllRecords(records);

            const nextDate = new Date(today.getFullYear(), today.getMonth() - 3, 1);
            currentYearRef.current = nextDate.getFullYear();
            currentMonthRef.current = nextDate.getMonth() + 1;

        } catch (error) {
            setError(`데이터를 불러오는 중 오류가 발생했습니다: ${error.message}`);
        } finally {
            setLoading(false);
            isLoadingRef.current = false;
        }
    };

    const scrollToBottom = () => {
        requestAnimationFrame(() => {
            listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        });
    };

    const loadMoreData = async () => {
        if (isLoadingRef.current || !hasMore) return;

        try {
            setLoadingMore(true);
            isLoadingRef.current = true;

            const year = currentYearRef.current;
            const month = currentMonthRef.current;

            const monthRecords = await fetchMonthData(year, month);

            // 중복 제거 후 실제 추가될 건수 확인
            const existingDates = new Set(allRecords.map(r => r.date));
            const newRecords = monthRecords.filter(r => !existingDates.has(r.date));

            if (newRecords.length > 0) {
                setAllRecords(prev => [...prev, ...newRecords]);
                // 데이터 추가 후 맨 아래로 스크롤
                setTimeout(scrollToBottom, 100);
            }

            // 다음 월 계산
            let nextYear = year;
            let nextMonth = month - 1;
            if (nextMonth < 1) {
                nextYear = year - 1;
                nextMonth = 12;
            }
            currentYearRef.current = nextYear;
            currentMonthRef.current = nextMonth;

            // 더 이상 데이터 없음 판단: 새 데이터가 0건이거나 1년 이상 과거
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

            if (newRecords.length === 0 || new Date(nextYear, nextMonth - 1) < oneYearAgo) {
                setHasMore(false);
            }

        } catch (error) {
            setError(`추가 데이터를 불러오는 중 오류가 발생했습니다: ${error.message}`);
        } finally {
            setLoadingMore(false);
            isLoadingRef.current = false;
        }
    };

    const handleRefresh = async () => {
        setAllRecords([]);
        setHasMore(true);
        currentYearRef.current = new Date().getFullYear();
        currentMonthRef.current = new Date().getMonth() + 1;
        setError(null);
        await loadInitialData();
    };

    useEffect(() => {
        if (isLoadingRef.current) return;
        loadInitialData();
    }, []);

    if (loading) {
        return (
            <>
                <HeadSub title="걸음수 전체 기록" />
                <div className="container">
                    <div className="inner-wrap" data-pad="normal">
                        <div style={{
                            textAlign: 'center',
                            padding: '50px 0',
                            color: '#666'
                        }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                border: '3px solid #f3f3f3',
                                borderTop: '3px solid #3498db',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                margin: '0 auto 20px'
                            }}></div>
                            <p>걸음수 기록을 불러오고 있습니다...</p>
                            <p style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
                                서버에서 데이터를 가져오는 중...
                            </p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <HeadSub title="걸음수 전체 기록" />

            <div className="container">
                <div className="inner-wrap" data-pad="normal">

                    {error && (
                        <div style={{
                            backgroundColor: '#f8d7da',
                            border: '1px solid #f5c6cb',
                            color: '#721c24',
                            padding: '15px',
                            margin: '0 0 20px 0',
                            borderRadius: '6px',
                            textAlign: 'center'
                        }}>
                            <p>{error}</p>
                            <button
                                onClick={handleRefresh}
                                style={{
                                    marginTop: '10px',
                                    padding: '8px 16px',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                다시 시도
                            </button>
                        </div>
                    )}

                    {!loading && !error && allRecords.length === 0 && (
                        <div style={{
                            textAlign: 'center',
                            padding: '50px 0',
                            color: '#666'
                        }}>
                            <p style={{ fontSize: '18px', marginBottom: '10px' }}>📊</p>
                            <p>아직 걸음수 기록이 없습니다.</p>
                            <p style={{ fontSize: '14px', color: '#999', marginBottom: '20px' }}>
                                앱을 사용하여 걸음수를 기록해보세요!
                            </p>
                            <button
                                onClick={handleRefresh}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#007AFF',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                새로고침
                            </button>
                        </div>
                    )}

                    {allRecords.length > 0 && (
                        <div className={style['stpes-infom-wrap']}>
                            {allRecords.map((record) => (
                                <div key={record.date} className={style["stpes-infom-box"]}>
                                    <div className={style["inner-box"]}>
                                        <ul>
                                            <li>
                                                <p>걸음수</p>
                                                <p><span>{record.steps.toLocaleString()}</span> 걸음</p>
                                            </li>
                                            <li>
                                                <p>소모 칼로리</p>
                                                <p><span>{record.calories}</span> kcal</p>
                                            </li>
                                            <li>
                                                <p>걸은 거리</p>
                                                <p><span>{record.distance}</span> km</p>
                                            </li>
                                        </ul>
                                        <span>{record.displayDate}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* 스크롤 앵커 */}
                    <div ref={listEndRef} />

                    {/* 더보기 버튼 */}
                    {hasMore && allRecords.length > 0 && (
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <button
                                onClick={loadMoreData}
                                disabled={loadingMore}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: loadingMore ? '#ccc' : '#007AFF',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: loadingMore ? 'not-allowed' : 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                {loadingMore ? '불러오는 중...' : '이전 기록 더보기'}
                            </button>
                        </div>
                    )}

                    {/* 더 이상 데이터 없음 */}
                    {!hasMore && allRecords.length > 0 && (
                        <div style={{
                            textAlign: 'center',
                            padding: '30px 0',
                            color: '#999',
                            fontSize: '14px'
                        }}>
                            <p>모든 기록을 불러왔습니다</p>
                        </div>
                    )}

                </div>
            </div>

            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </>
    );
};

export default StepsRecordList;